import { useState, forwardRef, useEffect } from 'react';
import { useFormik } from 'formik';
import { Modal, Backdrop } from "@mui/material";
import ReactGA from 'react-ga4';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useSpring, animated } from "react-spring";
import { ReactComponent as CloseButton } from "../../resources/close-button.svg";
import * as Checkbox from '@radix-ui/react-checkbox';
import { CheckboxIndicator } from '@radix-ui/react-checkbox';
import { CheckIcon } from '@radix-ui/react-icons';
import { useCaptcha } from "../../hooks/useCaptcha";

import formstyles from "../Form/Form.module.scss";
import styles from "./modal.module.scss";
import "../Form/selectstyles.scss";

const Fade = forwardRef((props, ref) => {
	const { in: open, children, onEnter, onExited, ...other } = props;
	const style = useSpring({
		from: { opacity: 0, transform: "translate3d(0,100%,0)" },
		to: { opacity: open ? 1 : 0, transform: "translate3d(0,0,0)" },
		onStart: () => {
			if (open && onEnter) {
				onEnter();
			}
		},
		onRest: () => {
			if (!open && onExited) {
				onExited();
			}
		},
	});

	return (
		<animated.div ref={ref} style={style} {...other}>
			{children}
		</animated.div>
	);
});


// Module-level variable that persists during navigation but resets on page reload
let hasShownModalThisSession = false;

const V1Modal = () => {
	const [showModal, setShowModal] = useState(false);
	const [displayThanks, setDisplayThanks] = useState(false);
	const getCaptchaToken = useCaptcha('submit_v1_modal');

	useEffect(() => {
		// Check if modal was permanently dismissed
		const permanentlyDismissed = sessionStorage.getItem('modal_dismissed') === 'true';

		// Only show if not permanently dismissed and not already shown during this navigation session
		if (!permanentlyDismissed && !hasShownModalThisSession) {
			setShowModal(true);
			hasShownModalThisSession = true;
		}
	}, []);

	const handleClose = () => {
		setShowModal(false);
		// Mark as temporarily dismissed for this session
		hasShownModalThisSession = true;
	};

	const handlePermanentDismiss = () => {
		setShowModal(false);
		// Mark as permanently dismissed in sessionStorage
		sessionStorage.setItem('modal_dismissed', 'true');
		hasShownModalThisSession = true;
	};

	const formik = useFormik({
		initialValues: {
			email: '',
			name: '',
			company: '',
			jobTitle: '',
			note: '',
			newsletter: true,
		},
		onSubmit: async (values) => {
			const token = await getCaptchaToken();
			const submitData = { 
				...values,
				'g-recaptcha-version': 'v3'
			};
			
			if (token) {
				submitData['g-recaptcha-response'] = token;
			}

			await fetch('https://usebasin.com/f/51b93d1bb059', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json; charset=UTF-8' },
				body: JSON.stringify(submitData)
			});
			setDisplayThanks(true);
		},
	});

	if (showModal) {
		ReactGA.event({
			category: "modal_popup",
			action: "popup viewed",
			label: "newsletter",
		});
	}

	return (
		<Modal
			className={styles.modal}
			open={showModal}
			onClose={handleClose}
			closeAfterTransition
			BackdropComponent={Backdrop}
			BackdropProps={{
				timeout: 500,
			}}
		>
			<Fade in={showModal} className={styles.modalOuter}>
				<div className={styles.inner} style={{
					maxWidth: '38rem',
					width: '90vw',
					maxHeight: '90vh',
					margin: '0 auto',
					position: 'relative',
					background: 'white',
					borderRadius: '8px',
					boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
				}}>
					<button
						className={styles.closeButton}
						onClick={handleClose}
						style={{
							position: 'absolute',
							top: '-10px',
							right: '-10px',
							zIndex: 10,
							border: 'none',
							borderRadius: '50%',
							cursor: 'pointer',
							padding: '5px'
						}}
					><CloseButton /></button>
					<div className={styles.modalContent}>
						<div>
							<div style={{ padding: '2rem 1.5rem', maxHeight: '80vh', overflowY: 'auto' }}>
								<div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
									<h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', lineHeight: '1.2' }}>Oxford Road + Veritone One</h2>
									<h3 style={{ fontSize: '1.1rem', color: 'var(--color-green)', marginBottom: '1rem', fontWeight: 'normal' }}>Now One. Still Oxford Road.</h3>
									<p style={{ fontSize: '0.9rem', lineHeight: '1.4', marginBottom: '0.5rem' }}>We’ve joined forces with Veritone One to create the most effective partner in audio and creator media. Combining Oxford Road’s culture-shaping strategy with Veritone One’s proprietary tech stack, Oxford Road is now home to the full legacy and capabilities of both agencies.</p>
									<p style={{ fontSize: '0.9rem', lineHeight: '1.4', marginBottom: '0.5rem' }}>This is the new standard for performance at scale in audio and video podcasting, streaming audio, radio, creator media, and beyond.</p>
									<p style={{ fontSize: '0.9rem', lineHeight: '1.4', marginBottom: '0.5rem', fontStyle: 'italic' }}>Where Culture Meets Conversion</p>
									<p style={{ fontSize: '0.9rem', lineHeight: '1.4', color: 'var(--color-green)', fontWeight: 'bold' }}>A new website is on the way. Want to be the first to know?</p>
								</div>
								<div className={styles.newsletterSignup}>
									{displayThanks ? (
										<div style={{ textAlign: 'center', padding: '1rem' }}>
											<h4 style={{ marginBottom: '0.5rem' }}>Thank you!</h4>
											<p style={{ fontSize: '0.9rem' }}>We'll be in touch soon with more details about the merger.</p>
										</div>
									) : (
										<form onSubmit={(e) => { e.preventDefault(); formik.handleSubmit(); }}>
											<Stack width="100%" spacing={1} mb={1} className={formstyles.inputWrapper}>
												<TextField
													type="text"
													id="name"
													name="name"
													label="NAME"
													size="small"
													required="required"
													value={formik.values.name}
													onChange={formik.handleChange}
												/>
												<TextField
													type="text"
													id="company"
													name="company"
													label="COMPANY"
													size="small"
													required="required"
													value={formik.values.company}
													onChange={formik.handleChange}
												/>
												<TextField
													type="text"
													id="jobTitle"
													name="jobTitle"
													label="JOB TITLE"
													size="small"
													required="required"
													value={formik.values.jobTitle}
													onChange={formik.handleChange}
												/>
												<TextField
													type="email"
													id="email"
													name="email"
													label="EMAIL ADDRESS"
													size="small"
													required="required"
													value={formik.values.email}
													onChange={formik.handleChange}
												/>
												<TextField
													type="text"
													id="note"
													name="note"
													label="NOTE"
													size="small"
													multiline
													rows={2}
													value={formik.values.note}
													onChange={formik.handleChange}
												/>
												<div className={formstyles.flex}>
													<Checkbox.Root className={formstyles.CheckboxRoot} defaultChecked id="newsletter" name="newsletter">
														<CheckboxIndicator className={formstyles.CheckboxIndicator}>
															<CheckIcon />
														</CheckboxIndicator>
													</Checkbox.Root>
													<label className={`${formstyles.Label} ${formstyles.defaultLabel} ${styles.checkboxLabel}`} htmlFor="newsletter">
														Sign up for our FREE weekly newsletter, The Influencer!
													</label>
												</div>
											</Stack>
											<div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', marginTop: '1rem', width: '100%' }}>
												<button
													type="button"
													style={{
														padding: '0.5rem 1rem',
														fontSize: '0.75rem',
														background: 'transparent',
														border: '1px solid #ccc',
														cursor: 'pointer',
														borderRadius: '4px'
													}}
													onClick={handlePermanentDismiss}
												>Don't show again</button>
												<button
													className={styles.next}
													type="submit"
													style={{ fontSize: '0.85rem' }}
													disabled={!formik.values.email || !formik.values.name || !formik.values.company || !formik.values.jobTitle}
												>Submit</button>
											</div>
										</form>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</Fade>
		</Modal>
	)
};

export default V1Modal;